import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { FileText, Loader2, Save, Plus, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { owner } from "@/lib/owner-api";
import api from "@/lib/api";

const emptyFeature = { title: "", desc: "" };
const emptyTestimonial = { name: "", role: "", quote: "" };
const emptyFaq = { question: "", answer: "" };

export default function OwnerCMS() {
  const [form, setForm] = useState({
    hero_headline: "", hero_subtitle: "", hero_cta_primary: "", hero_cta_secondary: "",
    pricing_note: "",
    features: [], testimonials: [], faq: [],
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/public/cms/landing-full").then((r) => {
      setForm({
        hero_headline: r.data.hero_headline || "",
        hero_subtitle: r.data.hero_subtitle || "",
        hero_cta_primary: r.data.hero_cta_primary || "",
        hero_cta_secondary: r.data.hero_cta_secondary || "",
        pricing_note: r.data.pricing_note || "",
        features: r.data.features || [],
        testimonials: r.data.testimonials || [],
        faq: r.data.faq || [],
      });
    }).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      // Strip _uid before sending (client-side only field)
      const strip = (arr) => arr.map((item) => {
        const clone = { ...item };
        delete clone._uid;
        return clone;
      });
      await owner.updateLandingFull({
        ...form,
        features: strip(form.features),
        testimonials: strip(form.testimonials),
        faq: strip(form.faq),
      });
      toast.success("CMS tersimpan");
    } catch (err) {
      console.warn("CMS save failed:", err);
      toast.error("Gagal simpan");
    }
    setSaving(false);
  };

  const addItem = (key, tmpl) => setForm({ ...form, [key]: [...form[key], { ...tmpl, _uid: `${key}-${Date.now()}-${Math.random().toString(36).slice(2, 8)}` }] });
  const removeItem = (key, idx) => setForm({ ...form, [key]: form[key].filter((_, i) => i !== idx) });
  const updateItem = (key, idx, field, value) => setForm({
    ...form,
    [key]: form[key].map((item, i) => i === idx ? { ...item, [field]: value } : item),
  });

  if (loading) return <div className="py-20 flex justify-center"><Loader2 className="animate-spin text-emerald-400" /></div>;

  return (
    <div className="space-y-6 max-w-4xl">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl font-bold text-white">CMS — Landing Page</h1>
          <p className="text-sm text-gray-500 mt-1">Edit semua section landing page: Hero, Features, Testimonials, FAQ.</p>
        </div>
        <Button onClick={save} disabled={saving} className="bg-emerald-600 hover:bg-emerald-500 text-white" data-testid="save-cms-btn">
          {saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Save className="mr-2 h-4 w-4" />}
          Simpan Semua
        </Button>
      </div>

      <Tabs defaultValue="hero" className="w-full">
        <TabsList className="bg-gray-900 border border-gray-800 grid grid-cols-4 w-full">
          <TabsTrigger value="hero" className="data-[state=active]:bg-emerald-600 data-[state=active]:text-white" data-testid="cms-tab-hero">Hero</TabsTrigger>
          <TabsTrigger value="features" className="data-[state=active]:bg-emerald-600 data-[state=active]:text-white" data-testid="cms-tab-features">Features</TabsTrigger>
          <TabsTrigger value="testimonials" className="data-[state=active]:bg-emerald-600 data-[state=active]:text-white" data-testid="cms-tab-testimonials">Testimonials</TabsTrigger>
          <TabsTrigger value="faq" className="data-[state=active]:bg-emerald-600 data-[state=active]:text-white" data-testid="cms-tab-faq">FAQ</TabsTrigger>
        </TabsList>

        <TabsContent value="hero" className="mt-5">
          <div className="bg-gray-900 border border-gray-800 rounded-xl p-6 space-y-4">
            <div className="flex items-center gap-2">
              <FileText className="text-emerald-400" size={18} />
              <h3 className="font-heading font-semibold text-white">Hero Section</h3>
            </div>
            <div>
              <Label className="text-gray-300">Headline utama</Label>
              <Textarea value={form.hero_headline} onChange={(e) => setForm({ ...form, hero_headline: e.target.value })} rows={3} className="bg-gray-800 border-gray-700 text-white" data-testid="cms-headline" />
            </div>
            <div>
              <Label className="text-gray-300">Subtitle</Label>
              <Textarea value={form.hero_subtitle} onChange={(e) => setForm({ ...form, hero_subtitle: e.target.value })} rows={3} className="bg-gray-800 border-gray-700 text-white" data-testid="cms-subtitle" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-gray-300">CTA Primary</Label>
                <Input value={form.hero_cta_primary} onChange={(e) => setForm({ ...form, hero_cta_primary: e.target.value })} className="bg-gray-800 border-gray-700 text-white" data-testid="cms-cta-primary" />
              </div>
              <div>
                <Label className="text-gray-300">CTA Secondary</Label>
                <Input value={form.hero_cta_secondary} onChange={(e) => setForm({ ...form, hero_cta_secondary: e.target.value })} className="bg-gray-800 border-gray-700 text-white" data-testid="cms-cta-secondary" />
              </div>
            </div>
          </div>
        </TabsContent>

        <TabsContent value="features" className="mt-5">
          <div className="bg-gray-900 border border-gray-800 rounded-xl p-6 space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="font-heading font-semibold text-white">Features ({form.features.length})</h3>
              <Button size="sm" onClick={() => addItem("features", emptyFeature)} className="bg-emerald-600 hover:bg-emerald-500 text-white" data-testid="add-feature-btn"><Plus className="mr-1 h-3 w-3" /> Tambah</Button>
            </div>
            {form.features.map((f, i) => (
              <div key={f._uid || `feature-${i}`} className="border border-gray-800 rounded p-3 space-y-2">
                <Input value={f.title || ""} onChange={(e) => updateItem("features", i, "title", e.target.value)} placeholder="Judul feature" className="bg-gray-800 border-gray-700 text-white" data-testid={`feature-title-${i}`} />
                <Textarea value={f.desc || ""} onChange={(e) => updateItem("features", i, "desc", e.target.value)} placeholder="Deskripsi feature" rows={2} className="bg-gray-800 border-gray-700 text-white" data-testid={`feature-desc-${i}`} />
                <Button size="sm" variant="outline" onClick={() => removeItem("features", i)} className="border-red-900 bg-red-950/30 text-red-400 hover:bg-red-900/50" data-testid={`remove-feature-${i}`}>
                  <Trash2 className="mr-1 h-3 w-3" /> Hapus
                </Button>
              </div>
            ))}
            {form.features.length === 0 && <div className="text-center py-8 text-sm text-gray-500">Belum ada. Klik "Tambah" untuk membuat feature card.</div>}
          </div>
        </TabsContent>

        <TabsContent value="testimonials" className="mt-5">
          <div className="bg-gray-900 border border-gray-800 rounded-xl p-6 space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="font-heading font-semibold text-white">Testimonials ({form.testimonials.length})</h3>
              <Button size="sm" onClick={() => addItem("testimonials", emptyTestimonial)} className="bg-emerald-600 hover:bg-emerald-500 text-white" data-testid="add-testimonial-btn"><Plus className="mr-1 h-3 w-3" /> Tambah</Button>
            </div>
            {form.testimonials.map((t, i) => (
              <div key={t._uid || `testi-${i}`} className="border border-gray-800 rounded p-3 space-y-2">
                <div className="grid grid-cols-2 gap-2">
                  <Input value={t.name || ""} onChange={(e) => updateItem("testimonials", i, "name", e.target.value)} placeholder="Nama" className="bg-gray-800 border-gray-700 text-white" data-testid={`testi-name-${i}`} />
                  <Input value={t.role || ""} onChange={(e) => updateItem("testimonials", i, "role", e.target.value)} placeholder="Role @brand" className="bg-gray-800 border-gray-700 text-white" data-testid={`testi-role-${i}`} />
                </div>
                <Textarea value={t.quote || ""} onChange={(e) => updateItem("testimonials", i, "quote", e.target.value)} placeholder="Quote/testimoni" rows={2} className="bg-gray-800 border-gray-700 text-white" data-testid={`testi-quote-${i}`} />
                <Button size="sm" variant="outline" onClick={() => removeItem("testimonials", i)} className="border-red-900 bg-red-950/30 text-red-400 hover:bg-red-900/50"><Trash2 className="mr-1 h-3 w-3" /> Hapus</Button>
              </div>
            ))}
            {form.testimonials.length === 0 && <div className="text-center py-8 text-sm text-gray-500">Belum ada testimonial.</div>}
          </div>
        </TabsContent>

        <TabsContent value="faq" className="mt-5">
          <div className="bg-gray-900 border border-gray-800 rounded-xl p-6 space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="font-heading font-semibold text-white">FAQ ({form.faq.length})</h3>
              <Button size="sm" onClick={() => addItem("faq", emptyFaq)} className="bg-emerald-600 hover:bg-emerald-500 text-white" data-testid="add-faq-btn"><Plus className="mr-1 h-3 w-3" /> Tambah</Button>
            </div>
            {form.faq.map((q, i) => (
              <div key={q._uid || `faq-${i}`} className="border border-gray-800 rounded p-3 space-y-2">
                <Input value={q.question || ""} onChange={(e) => updateItem("faq", i, "question", e.target.value)} placeholder="Pertanyaan" className="bg-gray-800 border-gray-700 text-white" data-testid={`faq-q-${i}`} />
                <Textarea value={q.answer || ""} onChange={(e) => updateItem("faq", i, "answer", e.target.value)} placeholder="Jawaban" rows={2} className="bg-gray-800 border-gray-700 text-white" data-testid={`faq-a-${i}`} />
                <Button size="sm" variant="outline" onClick={() => removeItem("faq", i)} className="border-red-900 bg-red-950/30 text-red-400 hover:bg-red-900/50"><Trash2 className="mr-1 h-3 w-3" /> Hapus</Button>
              </div>
            ))}
            {form.faq.length === 0 && <div className="text-center py-8 text-sm text-gray-500">Belum ada FAQ.</div>}
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
